import { Activity } from '../activity';
import { Agency, AgencyId } from '../agency';
import { Brand, BrandId } from '../brand';
import { FullAuditable, HasId, Omit } from '../common';
import { PathBuilder, ApiRoute, HttpMethod } from '../common/api/routes';
import { CurrencyCode } from '../common/lang/numbers';
import { Location } from '../location';
import { Platform, PlatformId, PlatformWithRequired } from '../platform/Platform';
import { UploadId } from '../upload';
import { BasicUserDto, User } from '../user/User';
import { ClientId } from './ClientId';

export interface Client extends HasId<ClientId>, FullAuditable {
    title: string;
    agencyId: AgencyId;
    brandId: BrandId | null;
    defaultSupportedPlannerLimit: number;
    facebookAdAccountId: string | null;
    facebookAdAccountName: string | null;
    four51Store: string | null;
    four51Company: string | null;
    four51SsoKey: string | null;
    country: string;
    fitwareBusinessCode: string | null;
    fitwareBusinessName: string | null;
    fitwareConversionAmountInCents: number;
    fitwareConversionCurrency: CurrencyCode;
    googleMyBusinessAccountName: string | null;
    currency: CurrencyCode;
    notificationEmailAddresses: string[];
    styleGuideUploadId: UploadId | null;
    billerCode: string | null;

    showGroupsOnDashboard: boolean;
    collectionLimit?: number | null;

    brand?: Brand;
    users?: User[];
    locations?: Location[];
    activities?: Activity[];
    agency?: Agency;

    graphqlId: string;
}

export type BasicClientInfo = Pick<Client, 'graphqlId' | 'id' | 'title'>;

function addClientApiRoot(path: PathBuilder<ClientApiParams>) {
    return path.literal('clients').param('clientId');
}

interface ClientApiParams {
    clientId: ClientId;
}

export interface Four51ClientConfig {
    four51Store: string;
    four51Company: string;
    four51SsoKey: string;
}

export interface UpdateFour51ConfigBody {
    four51Config: Four51ClientConfig | null;
}

export type GetPlatformsForClientsResult = Omit<PlatformWithRequired, 'id'> & {
    clientId: ClientId;
    platformId: PlatformId;
};

export interface UpdateNotificationEmailAddressesBody {
    notificationEmailAddresses: string[];
}

export type GetNotificationEmailAddressesResult = {
    notificationEmailAddresses: string[];
};

export type NewClientDto = Pick<Client, 'country' | 'title'>;

export const ClientApi = {
    createClient: new ApiRoute<Client, void, NewClientDto, void>(HttpMethod.Post, path => path.literal('clients')),
    /**
     * Returns all the users that can be added to the given client.
     * This can vary, depending if the managing user belongs to a a Primary or Secondary Agency.
     */
    getAddableUsers: new ApiRoute<BasicUserDto[], ClientApiParams, void, void>(HttpMethod.Get, path =>
        addClientApiRoot(path).literal('addableUsers'),
    ),
    getClientUsers: new ApiRoute<User[], ClientApiParams, void, void>(HttpMethod.Get, path =>
        addClientApiRoot(path).literal('users'),
    ),
    getClients: new ApiRoute<Client[], void, void, void>(HttpMethod.Get, path => path.literal('clients')),
    getNotificationEmailAddresses: new ApiRoute<GetNotificationEmailAddressesResult, ClientApiParams, void, void>(
        HttpMethod.Get,
        path => addClientApiRoot(path).literal('notificationEmailAddresses'),
    ),
    getPlatforms: new ApiRoute<Platform[], ClientApiParams, void, void>(HttpMethod.Get, path =>
        addClientApiRoot(path).literal('platforms'),
    ),
    getPlatformsForClients: new ApiRoute<GetPlatformsForClientsResult[], void, void, { clientIds: ClientId[] }>(
        HttpMethod.Get,
        path => path.literal('clients').literal('platforms'),
    ),
    updateFour51Config: new ApiRoute<void, ClientApiParams, UpdateFour51ConfigBody, void>(HttpMethod.Post, path =>
        addClientApiRoot(path).literal('four51Config'),
    ),
    updateNotificationEmailAddresses: new ApiRoute<void, ClientApiParams, UpdateNotificationEmailAddressesBody, void>(
        HttpMethod.Post,
        path => addClientApiRoot(path).literal('notificationEmailAddresses'),
    ),
};

export const CountryCodeNameMap = {
    AU: 'Australia',
    BE: 'Belgium',
    CA: 'Canada',
    DO: 'Dominican Republic',
    ES: 'Spain',
    GB: 'United Kingdom',
    GE: 'Georgia',
    ID: 'Indonesia',
    IE: 'Ireland',
    IN: 'India',
    JP: 'Japan',
    KR: 'South Korea',
    MM: 'Myanmar',
    MX: 'Mexico',
    NL: 'Netherlands',
    NZ: 'New Zealand',
    PH: 'Philippines',
    SA: 'Saudi Arabia',
    SG: 'Singapore',
    UE: 'United Arab Emirates',
    US: 'Unites States of America',
};
type CountryCodeNameMap = typeof CountryCodeNameMap;

export type CountryCode = keyof CountryCodeNameMap;

export function mapCountryCodeToCountryName(countryCode: CountryCode): string {
    if (countryCode in Object.keys(CountryCodeNameMap)) {
        return CountryCodeNameMap[countryCode];
    }
    return 'UNKNOWN';
}

export const DEFAULT_CLIENT_COLLECTION_LIMIT = 30;
